
import {
  AccountActivationPayload,
  ForgotPasswordPayload,
  ForgotPasswordResponse,
  IGetSignUpSettingResponse,
  SigninEmailPayload,
  SigninEmailResponse,
  SignupEmailPayload,
} from '../types/auth.type';
import fetchData from '@/api-client/fetcher';
import { consumeSocialRedirectUri, socialCallback } from '@/api-client/use-social-login';
export interface SignInResponse {
  access_token: string;
  refresh_token: string;
  enable_mfa: boolean;
  mfaId: string;
  mfaType: number;
}

export type PasswordSigninPayload = {
  grantType: 'password';
  username: string;
  password: string;
  captchaToken?: string;
};

export type SSoSigninPayload = {
  grantType: 'social';
  code: string;
  state: string;
  provider?: string;
  // mfa_id?: string;
  // mfa_code?: string;
  // mfa_type?: number;
  code_verifier?: string;
};

export type MFASigninPayload = {
  grantType: 'mfa_code';
  code: string;
  mfaId: string;
  mfaType: number;
};

export type MFASigninResponse = {
  access_token: string;
  refresh_token: string;
};

export interface SigninBySSOPayload {
  grantType: 'social';
  code: string;
  state: string;
  provider?: string;
  // mfa_id?: string;
  // mfa_code?: string;
  // mfa_type?: number;
  code_verifier?: string;
}

export interface SigninByBlocksOidcPayload {
  grantType: 'authorization_code';
  code: string;
}
export type SSoConsentSigninPayload = {
  grantType: 'sso_consent';
  code: string;
};

const projectKey = import.meta.env.VITE_OS_BLOCKS_X_BLOCKS_KEY || import.meta.env.VITE_X_BLOCKS_KEY || '';
const apiBaseUrl = import.meta.env.VITE_OS_BLOCKS_API_URL || import.meta.env.VITE_API_BASE_URL || '';

const getApiUrl = (path: string) => {
  const baseUrl = apiBaseUrl.endsWith('/') ? apiBaseUrl.slice(0, -1) : apiBaseUrl;

  const cleanPath = path.startsWith('/') ? path : `/${path}`;

  return `${baseUrl}${cleanPath}`;
};

export const signin = async <
  T extends 'password' | 'social' | 'mfa_code' | 'authorization_code' | 'sso_consent' = 'password',
>(
  payload:
    | PasswordSigninPayload
    | MFASigninPayload
    | SigninBySSOPayload
    | SigninByBlocksOidcPayload
    | SSoConsentSigninPayload
): Promise<
  T extends 'password' | 'social' | 'sso_consent' ? SignInResponse : MFASigninResponse
> => {
  const url = getApiUrl('/iam/v4/auth/login');

  // sign in flow
  if (payload.grantType === 'password') {
    return fetchData(url, {
      method: 'POST',
      body: {
        username: payload.username,
        password: payload.password,
        captcha_code: payload.captchaToken,
      },
    }, true);
  } else if (payload.grantType === 'social') {
    return socialCallback({
      code: payload.code,
      state: payload.state,
      provider: payload.provider || '',
      redirectUri: consumeSocialRedirectUri(),
      code_verifier: payload.code_verifier,
    });
  } else if (payload.grantType === 'authorization_code') {
    const signinBySSOData = new URLSearchParams();
    signinBySSOData.append('grant_type', 'authorization_code');
    signinBySSOData.append('code', payload.code);
    const response = await fetch(url, {
      method: 'POST',
      body: signinBySSOData,
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'x-blocks-key': projectKey,
      },
      credentials: 'include',
      referrerPolicy: 'no-referrer',
    });
    if (!response.ok) {
      const err = await response.json();
      throw new Error(err);
    }

    return response.json();
  } else if (payload.grantType === 'sso_consent') {
    const ssoConsentData = new URLSearchParams();
    ssoConsentData.append('grant_type', 'sso_consent');
    ssoConsentData.append('code', payload.code);

    // if (savedOrgId) {
    //   ssoConsentData.append('org_id', savedOrgId);
    // }
    const response = await fetch(url, {
      method: 'POST',
      body: ssoConsentData,
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'x-blocks-key': projectKey,
      },
      credentials: 'include',
      referrerPolicy: 'no-referrer',
    });
    if (!response.ok) {
      const err = await response.json();
      throw new Error(err);
    }

    return response.json();
  } else {
    // MFA OTP Verification flow
    const mfaFormData = new URLSearchParams();
    mfaFormData.append('grant_type', 'mfa_code');
    mfaFormData.append('code', payload.code || '');
    mfaFormData.append('mfa_id', payload.mfaId);
    mfaFormData.append('mfa_type', payload.mfaType.toString());

    const response = await fetch(url, {
      method: 'POST',
      body: mfaFormData,
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'x-blocks-key': projectKey,
      },
      credentials: 'include',
      referrerPolicy: 'no-referrer',
    });

    if (!response.ok) {
      const err = await response.json();
      throw new Error(err);
    }

    return response.json();
  }
};

export const signout = async (): Promise<{ isSuccess: true }> => {
  try {
    localStorage.removeItem('auth-storage');
    const url = '/iam/v4/auth/logout';
    return await fetchData(url, {
      method: "POST",
      body: JSON.stringify({})
    });
  } catch (error) {
    console.error('Logout operation failed:', error);
    throw error;
  }
};

export const getRefreshToken = async () => {
  return fetchData('/iam/v4/auth/refresh', {
    method: 'POST',
    body: {},
  });
};

export const accountActivation = async (data: AccountActivationPayload) => {
  const payload = {
    ...data,
    preventPostEvent: true,
    referrerPolicy: 'no-referrer',
  };
  const url = '/iam/v4/auth/activate';
  return fetchData(url, { body: JSON.stringify(payload), method: 'POST' });
};

export const forgotPassword = async (
  payload: ForgotPasswordPayload
): Promise<ForgotPasswordResponse> => {
  const modified = {
    ...payload,
    mailPurpose: 'RecoverAccount',
    referrerPolicy: 'no-referrer',
  };
  const url = '/iam/v4/auth/recover';
  return fetchData(url, { body: modified, method: 'POST' });
};

export const resetPassword = async (data: { code: string; password: string }) => {
  const payload = {
    ...data,
    logoutFromAllDevices: true,
    ProjectKey: projectKey,
    referrerPolicy: 'no-referrer',
  };

  const url = '/iam/v4/auth/reset-password';
  return fetchData(url, { body: payload, method: 'POST' });
};

export const resendActivation = async (data: { userId: string }) => {
  const payload = {
    ...data,
    mailPurpose: 'ResendActivation',
    referrerPolicy: 'no-referrer',
  };

  const url = '/iam/v4/auth/resend-activation';
  return fetchData(url, { body: payload, method: 'POST' });
};

export const logoutAll = async () => {
  const url = '/iam/v4/auth/logout-all';
  return fetchData(url, { method: "POST" });
};

export const signinByEmail = (payload: SigninEmailPayload): Promise<SigninEmailResponse> => {
  return fetchData(payload.username ? '/iam/v4/auth/login' : '/iam/v4/auth/login', {
    body: { username: payload.username, password: payload.password },
    method: 'POST',
  });
};

export const signupByEmail = (payload: SignupEmailPayload): Promise<SigninEmailResponse> => {
  const body = {
    email: payload.email,
    captchaCode: payload.captchaCode,
  };
  const url = '/iam/v4/auth/signup';
  return fetchData(url, {
    method: 'POST',
    body: JSON.stringify(body),
  });
};

export const getSignupSettings = (): Promise<IGetSignUpSettingResponse> => {
  return fetchData('/iam/v4/iam/signup-settings', { method: 'GET' });
};
